import { useExternalStoreRuntime } from '@assistant-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import {
    ChatCompletionTimeoutMs,
    ChatErrorAction,
    ChatErrorKind,
    ChatEventType,
    ChatRole,
    ChatStartTimeoutMs,
    CHAT_COMPLETED_TYPE,
    CHAT_FAILED_TYPE,
    CHAT_RECEIVE_MESSAGE_TYPE,
    CHAT_RESPONSE_COMPLETED_TYPE,
    CHAT_RESPONSE_DELTA_TYPE,
    CHAT_RETRIED_TYPE,
    CHAT_SEND_MESSAGE_TYPE,
    CHAT_STARTED_TYPE,
    EmptyAssistantDraft,
    UnsupportedRequestPatterns,
} from 'utils/chat/chatConstants';
import { getAiChatPayload, getLatestMessageContext, toAssistantMessages } from 'utils/chat/chatHelpers';

const normalizeFailureKind = (failureReason) => {
    if (typeof failureReason === 'object' && failureReason?.kind) {
        return failureReason.kind;
    }

    return failureReason || ChatErrorKind.GENERATION;
};

const isUnsupportedRequestError = (errorText = '') => {
    const normalizedErrorText = `${errorText}`.trim().toLowerCase();
    if (!normalizedErrorText) return false;

    return UnsupportedRequestPatterns.some((pattern) => normalizedErrorText.includes(pattern));
};

const buildAssistantMessageFromCompletion = ({ accumulatedText, fallbackMessageContext, payload }) => {
    const completedTimestamp = Date.now();

    return {
        id: payload?.message_id || payload?.id || uuid(),
        active: true,
        role: ChatRole.ASSISTANT,
        created_on: completedTimestamp,
        modified_on: completedTimestamp,
        message_text: payload?.message_text || accumulatedText,
        message_context: payload?.message_context || fallbackMessageContext,
    };
};

const isEventForActiveRequest = ({ activeRequestId, chatId, message, payload }) => {
    const payloadChatId = payload.chat_id || message.chat_id;
    if (payloadChatId !== chatId) return false;

    const eventRequestId =
        payload.request_id ||
        payload.requestId ||
        message.request_id ||
        message.requestId ||
        (message.type === CHAT_RECEIVE_MESSAGE_TYPE ? null : payload.id || message.id);

    if (!eventRequestId || !activeRequestId) return true;
    return eventRequestId === activeRequestId;
};

const ChatStreamUpdateMode = {
    APPEND: 'append',
    REPLACE: 'replace',
};

const getStreamTextUpdateFromRuntimeEvent = ({ message, payload, streamedText }) => {
    if (message.type === ChatEventType.LEGACY_DELTA) {
        const text = message.chunk?.message_text || '';
        return text ? { mode: ChatStreamUpdateMode.APPEND, text } : null;
    }

    if (message.type === CHAT_RESPONSE_DELTA_TYPE) {
        const text = message.delta || '';
        return text ? { mode: ChatStreamUpdateMode.APPEND, text } : null;
    }

    if (message.type === CHAT_RECEIVE_MESSAGE_TYPE) {
        const deltaText = payload.delta || payload.message_text_delta;
        if (deltaText) return { mode: ChatStreamUpdateMode.APPEND, text: deltaText };

        const messageText = payload.message_text || payload.chunk?.message_text || '';
        if (messageText && messageText !== streamedText) {
            return { mode: ChatStreamUpdateMode.REPLACE, text: messageText };
        }
    }

    return null;
};

const getFinalPayloadFromRuntimeEvent = ({ fallbackMessageContext, message, payload, streamedText }) => {
    if (message.type === ChatEventType.LEGACY_FINAL) {
        return {
            message_context: message.chunk?.message_context || fallbackMessageContext,
            message_id: message.chunk?.message_id,
            message_text: message.chunk?.message_text || streamedText,
        };
    }

    return payload;
};

const buildRetryRequestSnapshot = (userMessage) => {
    return {
        createdOn: userMessage.created_on,
        messageContext: userMessage.message_context,
        messageText: userMessage.message_text,
        userMessageId: userMessage.id,
    };
};

const buildRetryUserMessage = ({ fallbackMessageContext, retryRequest }) => {
    return {
        id: retryRequest.userMessageId,
        active: true,
        created_on: retryRequest.createdOn,
        modified_on: Date.now(),
        role: ChatRole.USER,
        message_text: retryRequest.messageText,
        message_context: retryRequest.messageContext || fallbackMessageContext,
    };
};

const buildOutgoingChatRequest = ({ assignmentId, chatId, fileKey, requestId, userMessage }) => {
    return {
        type: CHAT_SEND_MESSAGE_TYPE,
        id: requestId,
        chat_id: chatId,
        assignment_id: assignmentId,
        created_on: userMessage.created_on,
        file_key: fileKey,
        message_id: userMessage.id,
        message_text: userMessage.message_text,
    };
};

const buildTransientAssistantMessage = ({ createdOn, id, text }) => {
    return {
        active: true,
        created_on: createdOn,
        id,
        message_context: null,
        message_text: text,
        modified_on: createdOn,
        role: ChatRole.ASSISTANT,
    };
};

const getOutOfSyncChatReconciliation = ({ chatId, message }) => {
    if (message.type !== 'pong' || !Array.isArray(message.chat_reconciliations) || !chatId) return null;

    return (
        message.chat_reconciliations.find(({ chat_id: reconciledChatId, out_of_sync: outOfSync }) => {
            return reconciledChatId === chatId && outOfSync;
        }) || null
    );
};

/**
 * Runtime adapter between persisted chat data, websocket AI events, and assistant-ui.
 *
 * Use inside `AssistantChatRuntime`, not directly from page code. It turns persisted chat
 * messages into assistant-ui messages, sends user prompts over websocket, streams assistant
 * drafts, handles retries/errors, and reports persisted state changes back to the thread hook.
 *
 * @param {Object} props
 * @param {Object|null} props.chat Persisted chat with messages and processing state.
 * @param {string} [props.fileKey] Current submission file key sent with user prompts.
 * @param {boolean} props.isConnected Whether the websocket is currently connected.
 * @param {Function} props.onChatRequestStateChange Receives optimistic persistence events for the thread hook.
 * @param {Function} props.onMessage Websocket subscription function.
 * @param {Function} props.sendMessage Websocket send function.
 * @returns {{chatRuntimeContextValue: Object, runtime: Object}} Context value and assistant-ui runtime.
 */
const useAssistantChatController = ({
    chat,
    fileKey,
    isConnected,
    onChatRequestStateChange,
    onMessage,
    sendMessage,
}) => {
    const { t } = useTranslation();
    const assistantMessages = useMemo(() => toAssistantMessages(chat?.messages || []), [chat?.messages]);
    const chatId = chat?.id;
    const assignmentId = chat?.assignment_id;
    const chatIsProcessing = Boolean(chat?.is_processing);
    const latestMessageContext = useMemo(() => getLatestMessageContext(chat?.messages || []), [chat?.messages]);

    const [chatError, setChatError] = useState(null);
    const [manualAssistantDraft, setManualAssistantDraft] = useState(EmptyAssistantDraft);
    const [isRetrying, setIsRetrying] = useState(false);
    const [runtimeIsRunning, setRuntimeIsRunning] = useState(false);

    const activeRequestRef = useRef(null);
    const transientAssistantMessageRef = useRef(null);
    const isConnectedRef = useRef(isConnected);
    const retryRequestRef = useRef(null);

    useEffect(() => {
        isConnectedRef.current = isConnected;
    }, [isConnected]);

    const buildChatError = useCallback(
        (kind) => {
            if (kind === ChatErrorKind.DISCONNECTED) {
                return {
                    actionLabel: t('controls.refresh', 'Refresh'),
                    actionType: ChatErrorAction.REFRESH,
                    icon: 'resync',
                    kind,
                    message: t('errors.chat-disconnect', 'Chat has been disconnected, please refresh your page'),
                };
            }

            if (kind === ChatErrorKind.UNSUPPORTED_REQUEST) {
                return {
                    actionLabel: null,
                    actionType: null,
                    icon: null,
                    kind,
                    message: t(
                        'errors.unsupported-request',
                        "Sorry, we couldn't help with your request. Try asking another question about your feedback."
                    ),
                };
            }

            return {
                actionLabel: t('controls.regenerate', 'Regenerate'),
                actionType: ChatErrorAction.REGENERATE,
                icon: 'refresh',
                kind: ChatErrorKind.GENERATION,
                message: t('message.something-went-wrong-try-again', 'Something went wrong, please try again'),
            };
        },
        [t]
    );

    const clearChatError = useCallback(() => {
        setChatError(null);
    }, []);

    const clearActiveRequest = useCallback(() => {
        const activeRequest = activeRequestRef.current;
        if (!activeRequest) return;

        if (activeRequest.startTimeoutId) {
            clearTimeout(activeRequest.startTimeoutId);
        }

        if (activeRequest.stallTimeoutId) {
            clearTimeout(activeRequest.stallTimeoutId);
        }

        if (activeRequest.unsubscribe) {
            activeRequest.unsubscribe();
        }

        activeRequestRef.current = null;
    }, []);

    const resetTransientRuntimeState = useCallback(() => {
        setIsRetrying(false);
        setManualAssistantDraft(EmptyAssistantDraft);
        setRuntimeIsRunning(false);
    }, []);

    const resetRuntimeForChatReconciliation = useCallback(() => {
        clearActiveRequest();
        clearChatError();
        resetTransientRuntimeState();
        transientAssistantMessageRef.current = null;
    }, [clearActiveRequest, clearChatError, resetTransientRuntimeState]);

    const applyRequestFailure = useCallback(
        (failureReason) => {
            const failureKind = normalizeFailureKind(failureReason);
            clearActiveRequest();
            resetTransientRuntimeState();
            onChatRequestStateChange?.({
                type: 'request_failed',
                failureKind,
            });
            setChatError(buildChatError(failureKind));
        },
        [buildChatError, clearActiveRequest, onChatRequestStateChange, resetTransientRuntimeState]
    );

    const executeRequest = useCallback(
        ({ onFailure, onFinal, onStreamUpdate, shouldCreateUserMessage, userMessage }) => {
            if (!chatId || !sendMessage) {
                onFailure?.(ChatErrorKind.GENERATION);
                return Promise.resolve(null);
            }

            setRuntimeIsRunning(true);
            transientAssistantMessageRef.current = {
                createdOn: Date.now(),
                id: uuid(),
            };

            return new Promise((resolve) => {
                const requestId = uuid();
                let accumulatedStreamText = EmptyAssistantDraft;

                const finalizeFailedRequest = (failureReason) => {
                    clearActiveRequest();
                    setRuntimeIsRunning(false);
                    transientAssistantMessageRef.current = null;
                    onFailure?.(failureReason);
                    resolve(null);
                };

                const finalizeSuccessfulRequest = (payload) => {
                    const assistantMessage = buildAssistantMessageFromCompletion({
                        accumulatedText: accumulatedStreamText,
                        fallbackMessageContext: latestMessageContext,
                        payload,
                    });

                    clearActiveRequest();
                    setRuntimeIsRunning(false);
                    transientAssistantMessageRef.current = null;
                    onFinal?.(assistantMessage);
                    resolve(assistantMessage);
                };

                const refreshCompletionTimeout = () => {
                    const activeRequest = activeRequestRef.current;
                    if (!activeRequest) return;

                    if (activeRequest.stallTimeoutId) {
                        clearTimeout(activeRequest.stallTimeoutId);
                    }

                    activeRequest.stallTimeoutId = setTimeout(() => {
                        finalizeFailedRequest(
                            isConnectedRef.current ? ChatErrorKind.GENERATION : ChatErrorKind.DISCONNECTED
                        );
                    }, ChatCompletionTimeoutMs);
                };

                const unsubscribe = onMessage((message) => {
                    const payload = getAiChatPayload(message);
                    if (
                        !isEventForActiveRequest({
                            activeRequestId: activeRequestRef.current?.requestId,
                            chatId,
                            message,
                            payload,
                        })
                    ) {
                        return;
                    }

                    if (message.type === CHAT_STARTED_TYPE) {
                        if (activeRequestRef.current?.startTimeoutId) {
                            clearTimeout(activeRequestRef.current.startTimeoutId);
                            activeRequestRef.current.startTimeoutId = null;
                        }

                        refreshCompletionTimeout();
                        return;
                    }

                    if (message.type === CHAT_RETRIED_TYPE) {
                        refreshCompletionTimeout();
                        return;
                    }

                    if (message.type === CHAT_FAILED_TYPE) {
                        finalizeFailedRequest(
                            isUnsupportedRequestError(payload.error)
                                ? ChatErrorKind.UNSUPPORTED_REQUEST
                                : ChatErrorKind.GENERATION
                        );
                        return;
                    }

                    const streamTextUpdate = getStreamTextUpdateFromRuntimeEvent({
                        message,
                        payload,
                        streamedText: accumulatedStreamText,
                    });
                    if (streamTextUpdate) {
                        accumulatedStreamText =
                            streamTextUpdate.mode === ChatStreamUpdateMode.REPLACE
                                ? streamTextUpdate.text
                                : `${accumulatedStreamText}${streamTextUpdate.text}`;
                        refreshCompletionTimeout();
                        onStreamUpdate?.(streamTextUpdate);

                        if (!payload.done) return;
                    }

                    if (
                        message.type === ChatEventType.LEGACY_FINAL ||
                        message.type === CHAT_RESPONSE_COMPLETED_TYPE ||
                        message.type === CHAT_COMPLETED_TYPE ||
                        (message.type === CHAT_RECEIVE_MESSAGE_TYPE && payload.done)
                    ) {
                        finalizeSuccessfulRequest(
                            getFinalPayloadFromRuntimeEvent({
                                fallbackMessageContext: latestMessageContext,
                                message,
                                payload,
                                streamedText: accumulatedStreamText,
                            })
                        );
                    }
                });

                activeRequestRef.current = {
                    fail: (failureReason) => finalizeFailedRequest(failureReason),
                    requestId,
                    startTimeoutId: null,
                    stallTimeoutId: null,
                    unsubscribe,
                };

                const didSendRequest = sendMessage(
                    buildOutgoingChatRequest({
                        assignmentId,
                        chatId,
                        fileKey,
                        requestId,
                        userMessage,
                    })
                );

                if (!didSendRequest) {
                    finalizeFailedRequest(ChatErrorKind.DISCONNECTED);
                    return;
                }

                retryRequestRef.current = buildRetryRequestSnapshot(userMessage);
                clearChatError();

                if (shouldCreateUserMessage) {
                    onChatRequestStateChange?.({
                        type: 'user_message_created',
                        userMessage,
                    });
                }

                activeRequestRef.current.startTimeoutId = setTimeout(() => {
                    finalizeFailedRequest(
                        isConnectedRef.current ? ChatErrorKind.GENERATION : ChatErrorKind.DISCONNECTED
                    );
                }, ChatStartTimeoutMs);
            });
        },
        [
            assignmentId,
            chatId,
            clearActiveRequest,
            clearChatError,
            fileKey,
            latestMessageContext,
            onMessage,
            onChatRequestStateChange,
            sendMessage,
        ]
    );

    useEffect(() => {
        if (isConnected || !activeRequestRef.current?.fail) return;
        activeRequestRef.current.fail(ChatErrorKind.DISCONNECTED);
    }, [isConnected]);

    useEffect(() => {
        return () => {
            clearActiveRequest();
        };
    }, [clearActiveRequest]);

    useEffect(() => {
        const cleanup = onMessage((message) => {
            if (!getOutOfSyncChatReconciliation({ chatId, message })) return;
            resetRuntimeForChatReconciliation();
        });

        return cleanup;
    }, [chatId, onMessage, resetRuntimeForChatReconciliation]);

    useEffect(() => {
        clearActiveRequest();
        setChatError(null);
        setManualAssistantDraft(EmptyAssistantDraft);
        setIsRetrying(false);
        setRuntimeIsRunning(false);
        transientAssistantMessageRef.current = null;
        retryRequestRef.current = null;
    }, [chatId, clearActiveRequest]);

    const onNewMessage = useCallback(
        async (message) => {
            const messageText = message.content
                .filter((part) => part.type === 'text')
                .map((part) => part.text || '')
                .join('\n\n')
                .trim();
            if (!chatId || !messageText) return;

            clearChatError();
            setManualAssistantDraft(EmptyAssistantDraft);

            const timestamp = message.createdAt instanceof Date ? message.createdAt.getTime() : Date.now();

            await executeRequest({
                onStreamUpdate: ({ mode, text }) => {
                    setManualAssistantDraft((currentDraft) =>
                        mode === ChatStreamUpdateMode.REPLACE ? text : `${currentDraft}${text}`
                    );
                },
                onFailure: (failureReason) => {
                    applyRequestFailure(failureReason);
                },
                onFinal: (assistantMessage) => {
                    setManualAssistantDraft(EmptyAssistantDraft);
                    onChatRequestStateChange?.({
                        type: 'assistant_message_created',
                        assistantMessage,
                    });
                },
                shouldCreateUserMessage: true,
                userMessage: {
                    id: uuid(),
                    active: true,
                    created_on: timestamp,
                    modified_on: timestamp,
                    role: ChatRole.USER,
                    message_text: messageText,
                    message_context: latestMessageContext,
                },
            });
        },
        [applyRequestFailure, chatId, clearChatError, executeRequest, latestMessageContext, onChatRequestStateChange]
    );

    const retryLastRequest = useCallback(() => {
        const retryRequest = retryRequestRef.current;
        if (!retryRequest) return;

        clearChatError();
        setIsRetrying(true);
        setManualAssistantDraft(EmptyAssistantDraft);

        executeRequest({
            onStreamUpdate: ({ mode, text }) => {
                setManualAssistantDraft((currentDraft) =>
                    mode === ChatStreamUpdateMode.REPLACE ? text : `${currentDraft}${text}`
                );
            },
            onFailure: (failureReason) => {
                applyRequestFailure(failureReason);
            },
            onFinal: (assistantMessage) => {
                setIsRetrying(false);
                setManualAssistantDraft(EmptyAssistantDraft);
                onChatRequestStateChange?.({
                    type: 'assistant_message_created',
                    assistantMessage,
                });
            },
            shouldCreateUserMessage: false,
            userMessage: buildRetryUserMessage({
                fallbackMessageContext: latestMessageContext,
                retryRequest,
            }),
        });
    }, [applyRequestFailure, clearChatError, executeRequest, latestMessageContext, onChatRequestStateChange]);

    const chatRuntimeContextValue = useMemo(() => {
        return {
            chatError,
            clearChatError,
            isRetrying,
            manualAssistantDraft,
            retryLastRequest,
        };
    }, [chatError, clearChatError, isRetrying, manualAssistantDraft, retryLastRequest]);

    const runtimeMessages = useMemo(() => {
        if (!manualAssistantDraft.trim() || !transientAssistantMessageRef.current) {
            return assistantMessages;
        }

        return toAssistantMessages([
            ...(chat?.messages || []),
            buildTransientAssistantMessage({
                createdOn: transientAssistantMessageRef.current.createdOn,
                id: transientAssistantMessageRef.current.id,
                text: manualAssistantDraft,
            }),
        ]);
    }, [assistantMessages, chat?.messages, manualAssistantDraft]);

    const runtime = useExternalStoreRuntime({
        isRunning: runtimeIsRunning || isRetrying || (chatIsProcessing && !chatError),
        messages: runtimeMessages,
        onNew: onNewMessage,
    });

    return {
        chatRuntimeContextValue,
        runtime,
    };
};

export default useAssistantChatController;
